import { useState, useEffect, useRef } from 'react'
import { AnimatePresence, MotionConfig, motion } from 'framer-motion'
import type { ScreenPhase } from '@gymboard/types'
import { getScreenToken, getRoomId, getTenantInfo, getScreenInfo } from './lib/storage'
import { setSentryScreenContext } from './lib/sentry'
import { useScreenSession } from './hooks/useScreenSession'

/**
 * Returns true only while scheduledAt is in the future AND within thresholdMins.
 *
 *   false  ──────────────[  true  ]──── false (once past)
 *              -thresholdMins      0
 *
 * When the countdown hits 0 this flips to false, so the split screen
 * disappears automatically instead of sticking at "00:00".
 */
function useIsWithinMinutes(scheduledAt: string | null | undefined, thresholdMins: number): boolean {
  const [within, setWithin] = useState(false)

  useEffect(() => {
    if (!scheduledAt) { setWithin(false); return }

    const targetMs = new Date(scheduledAt).getTime()
    if (isNaN(targetMs)) { setWithin(false); return }

    const check = () => {
      const diff = targetMs - Date.now()
      // Stay true for a 30-second grace period after diff hits 0 so the split
      // screen bridges the gap between "0:00" and the server auto-starting the
      // next session (a small amount of backend delay is expected).
      setWithin(diff >= -30_000 && diff <= thresholdMins * 60_000)
    }

    check()
    // Poll every 2 s — fine-grained enough that the grace-window edge transitions
    // reliably within a second or two (vs. 10 s lag previously).
    const id = setInterval(check, 2_000)
    return () => clearInterval(id)
  }, [scheduledAt, thresholdMins])

  return within
}

/**
 * Returns true for `windowMins` minutes after `endedAt`.
 * Used to keep the handoff split screen visible after class A ends and
 * class B goes LIVE — so athletes from class A can still see who won.
 *
 *   false  ────[  true  ]──────────────────── false
 *           endedAt   endedAt + windowMins
 */
function useHandoffActive(endedAt: number | null, windowMins: number): boolean {
  const [active, setActive] = useState(false)

  useEffect(() => {
    if (!endedAt) { setActive(false); return }

    const check = () => {
      const elapsed = Date.now() - endedAt
      setActive(elapsed >= 0 && elapsed <= windowMins * 60_000)
    }

    check()
    const id = setInterval(check, 5_000)
    return () => clearInterval(id)
  }, [endedAt, windowMins])

  return active
}

// Screens (lazy-ish — still eagerly imported for TV reliability)
import PairingScreen from './screens/PairingScreen'
import AmbientScreen from './screens/AmbientScreen'
import PreClassScreen from './screens/PreClassScreen'
import LiveLeaderboardScreen from './screens/LiveLeaderboardScreen'
import EndedScreen from './screens/EndedScreen'
import SplitScreen from './screens/SplitScreen'
import CheckInKioskScreen from './screens/CheckInKioskScreen'
import CountInCard from './components/CountInCard'

/**
 * Tracks the PRESTART → LIVE edge so we can show a "3-2-1-GO!" overlay for
 * the first ~3.5 s of LIVE. We fire on the exact session status flip (not on
 * initial mount with LIVE) so reconnecting mid-class doesn't trigger it.
 */
function usePrestartToLiveEdge(status: string | null): [boolean, () => void] {
  const [show, setShow] = useState(false)
  const prevRef = useRef<string | null>(null)

  useEffect(() => {
    if (prevRef.current === 'PRESTART' && status === 'LIVE') setShow(true)
    prevRef.current = status
  }, [status])

  return [show, () => setShow(false)]
}

export default function App() {
  const [screenToken, setScreenToken] = useState<string | null>(() =>
    getScreenToken()
  )
  // roomId must also update after pairing (pairing writes it to localStorage)
  const [roomId, setRoomId] = useState<string | null>(() => getRoomId())

  // Listen for storage changes (e.g., after pairing on same tab)
  useEffect(() => {
    const onStorage = () => {
      setScreenToken(getScreenToken())
      setRoomId(getRoomId())
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  // Tag Sentry with this screen's identity once paired, so a crash report
  // from a fleet of gym TVs says WHICH screen in WHICH room fell over.
  // Re-runs on re-pair (roomId/screenToken change). No-op without a DSN.
  useEffect(() => {
    if (!screenToken) return
    const tenant = getTenantInfo()
    const screen = getScreenInfo()
    setSentryScreenContext({
      screenName: screen?.screenName ?? null,
      roomId,
      tenantSlug: tenant?.name ?? null,
    })
  }, [screenToken, roomId])

  const sessionState = useScreenSession(screenToken ? roomId : null)

  const {
    sessionStatus,
    session,
    leaderboard,
    roster,
    qrToken,
    qrSignature,
    qrExpiresAt,
    overtakeEvent,
    comebackEvent,
    shoutoutEvent,
    timer,
    clockOffsetMs,
    isConnected,
    isReconnecting,
    elapsedMs,
    upcomingSession,
    upcomingQrToken,
    upcomingQrSignature,
    upcomingQrExpiresAt,
    upcomingRoster,
    previousSession,
    previousLeaderboard,
    previousEndedAt,
    program,
  } = sessionState

  // The upcoming class's scheduledAt — used to decide when to split the screen
  const upcomingScheduledAt =
    (upcomingSession?.classInstance as any)?.scheduledAt as string | undefined

  // Split screen appears when the next class is ≤ 5 minutes away
  const upcomingIsWithin5Min = useIsWithinMinutes(upcomingScheduledAt, 5)

  // Handoff split: show class A's final results + class B's live leaderboard
  // for 5 minutes after class A ended (so athletes can see who won).
  // Handoff window is 3 minutes (per product spec): right after class A ends we
  // keep A's final leaderboard on the left and class B's live leaderboard on
  // the right so athletes see who won before the full class-B display takes over.
  const handoffActive = useHandoffActive(previousEndedAt, 3)

  // PRESTART → LIVE edge: play a 3-2-1-GO overlay on top of whichever screen
  // is rendering. Parent owns the visibility; the card calls us back when done.
  const [showCountIn, hideCountIn] = usePrestartToLiveEdge(sessionStatus as string | null)

  // ─── State machine ────────────────────────────────────────────────────────────
  // Phase key drives BOTH the switch below and the cross-dissolve: when the
  // key changes, AnimatePresence fades/settles the old screen out while the
  // new one fades in — Apple's cross-dissolve-with-a-whisper-of-scale, not
  // a hard cut. Pairing participates like any other phase.
  // A screen paired without a room is an entrance check-in screen.
  const phaseKey: ScreenPhase | 'LOADING' | 'PAIRING' | 'KIOSK' = !screenToken
    ? 'PAIRING'
    : !roomId
    ? 'KIOSK'
    : program?.phase ?? inferLegacyPhase()

  const screen = renderScreen()

  return (
    // `reducedMotion="user"` disables non-essential transforms system-wide
    // when the OS asks for reduced motion (framer keeps opacity fades).
    <MotionConfig reducedMotion="user">
      <Frame className="relative">
        <AnimatePresence initial={false}>
          <motion.div
            key={phaseKey}
            className="absolute inset-0"
            // Apple's sheet curve: fast start, long soft landing. Incoming
            // rises from 98.5% scale; outgoing drifts slightly PAST 100% —
            // the "camera moves forward" depth cue iOS uses everywhere.
            initial={{ opacity: 0, scale: 0.985 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.012 }}
            transition={{ duration: 0.55, ease: [0.32, 0.72, 0, 1] }}
          >
            {screen}
          </motion.div>
        </AnimatePresence>
        {showCountIn && <CountInCard onDone={hideCountIn} />}
      </Frame>
    </MotionConfig>
  )

  // Legacy phase inference — the pre-Task-#8 client-side state machine.
  // Used ONLY when the server program hasn't loaded yet (cold boot, before
  // the first fetch/socket frame). Once `program` arrives it is
  // authoritative and these client timers (handoffActive,
  // upcomingIsWithin5Min) no longer decide the screen.
  function inferLegacyPhase(): ScreenPhase | 'LOADING' {
    if (!sessionStatus && upcomingSession) return 'PRECLASS'
    if (!sessionStatus) return 'AMBIENT'
    if (sessionStatus === 'PRESTART') return 'PRECLASS'
    if (sessionStatus === 'LIVE' || sessionStatus === 'PAUSED') {
      if (previousSession && handoffActive) return 'HANDOFF_SPLIT'
      if (upcomingSession && upcomingIsWithin5Min) return 'UPCOMING_SPLIT'
      return 'LIVE'
    }
    if (sessionStatus === 'ENDED' && upcomingSession) return 'ENDED_SPLIT'
    if (sessionStatus === 'ENDED') return 'ENDED'
    return 'LOADING'
  }

  // Screens render bare — the single <Frame> lives in the return above so
  // the cross-dissolve happens INSIDE one stable frame (Apple transitions
  // never fade the chrome, only the content).
  function renderScreen() {
    // The pre-class session: the main session when it's the PRESTART one,
    // else the upcoming slot (idle-room-with-upcoming case). QR/roster only
    // apply when we're rendering the main session.
    const preClassIsMain = sessionStatus === 'PRESTART' && !!session
    const preClassSession = preClassIsMain ? session : upcomingSession

    switch (phaseKey) {
      // No screen token → pairing (client-only, never server-driven).
      case 'PAIRING':
        return (
          <PairingScreen
            onPaired={() => {
              setScreenToken(getScreenToken())
              setRoomId(getRoomId())
            }}
          />
        )

      case 'AMBIENT':
        return <AmbientScreen roomId={roomId} />

      case 'KIOSK':
        return <CheckInKioskScreen whenOff={<AmbientScreen roomId={null} />} />

      case 'PRECLASS':
        return (
          <PreClassScreen
            session={preClassSession}
            qrToken={preClassIsMain ? qrToken : null}
            qrSignature={preClassIsMain ? qrSignature : null}
            qrExpiresAt={preClassIsMain ? qrExpiresAt : null}
            roster={preClassIsMain ? roster : []}
          />
        )

      // Handoff: class A just ended, class B is live — A's final board on
      // the left, B's live board on the right.
      case 'HANDOFF_SPLIT':
        return (
          <SplitScreen
            endedSession={previousSession}
            endedLeaderboard={previousLeaderboard}
            liveSession={session}
            liveLeaderboard={leaderboard}
          />
        )

      // Live + next class within the split threshold — live on the left,
      // upcoming QR check-in on the right.
      case 'UPCOMING_SPLIT':
        return (
          <SplitScreen
            endedSession={session}
            endedLeaderboard={leaderboard}
            upcomingSession={upcomingSession}
            upcomingQrToken={upcomingQrToken}
            upcomingQrSignature={upcomingQrSignature}
            upcomingQrExpiresAt={upcomingQrExpiresAt}
            upcomingRoster={upcomingRoster}
            isLive
          />
        )

      case 'LIVE':
        // The server folds LIVE and PAUSED into one phase; the screen still
        // paints its pause overlay because we pass the real session status
        // (LIVE/PAUSED) through — the pause UI keys off status, not phase.
        return (
          <LiveLeaderboardScreen
            session={session}
            sessionStatus={sessionStatus ?? 'LIVE'}
            leaderboard={leaderboard}
            qrToken={qrToken}
            qrSignature={qrSignature}
            qrExpiresAt={qrExpiresAt}
            overtakeEvent={overtakeEvent}
            comebackEvent={comebackEvent}
            shoutoutEvent={shoutoutEvent}
            timer={timer}
            clockOffsetMs={clockOffsetMs}
            isConnected={isConnected}
            isReconnecting={isReconnecting}
            elapsedMs={elapsedMs}
          />
        )

      // Ended A + upcoming B — A's final board on the left, B's QR on the right.
      case 'ENDED_SPLIT':
        return (
          <SplitScreen
            endedSession={session}
            endedLeaderboard={leaderboard}
            upcomingSession={upcomingSession}
            upcomingQrToken={upcomingQrToken}
            upcomingQrSignature={upcomingQrSignature}
            upcomingQrExpiresAt={upcomingQrExpiresAt}
            upcomingRoster={upcomingRoster}
          />
        )

      case 'ENDED':
        return (
          <EndedScreen
            session={session}
            leaderboard={leaderboard}
            onDismiss={() => window.location.reload()}
          />
        )

      default:
        return (
          <div className="w-full h-full flex items-center justify-center">
            <p className="text-white/40 text-2xl">Loading…</p>
          </div>
        )
    }
  }
}

/** Full-screen dark frame wrapper shared by every TV screen. */
function Frame({
  children,
  className = '',
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={`w-screen h-screen overflow-hidden bg-[#0E1022] ${className}`}>
      {children}
    </div>
  )
}
